import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import type { SiteConfig } from '@bosamie/shared';

// ===== Image with Fallback =====
export function ImageFB({ src, alt, className }: { src: string | null; alt: string; className?: string }) {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div className={`img-fallback ${className || ''}`}>
        <svg className="ph-ico" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M8.5 11.5h15l1.4 14.2a2 2 0 0 1-2 2.3H9.1a2 2 0 0 1-2-2.3L8.5 11.5Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path d="M12 11.5V9.6a4 4 0 0 1 8 0v1.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M13.5 18h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
        <span className="ph-txt">No Image</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      draggable={false}
      loading="lazy"
      onError={() => setError(true)}
      onDragStart={(e) => e.preventDefault()}
    />
  );
}

// ===== TopBar =====
export function TopBar({ title, showBack, onBack }: { title?: string; showBack?: boolean; onBack?: () => void }) {
  return (
    <header className="topbar">
      {showBack && (
        <button className="back" onClick={onBack ?? (() => window.history.back())} aria-label="Back">
          ‹
        </button>
      )}
      <div className="brand-label">
        <span className="dot" />
        Bosamie <strong>Myanmar</strong>
      </div>
      {title && <span className="ttl">{title}</span>}
    </header>
  );
}

// ===== Breadcrumb =====
export function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="crumbs">
      {items.map((item, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s-1)' }}>
          {item.to ? <Link to={item.to}>{item.label}</Link> : <span className="cur">{item.label}</span>}
          {i < items.length - 1 && <span className="sep">›</span>}
        </span>
      ))}
    </nav>
  );
}

// ===== FAB (Floating Action Button) - only on home =====
export function FAB({ onClick }: { onClick: () => void }) {
  const location = useLocation();
  if (location.pathname !== '/') return null;

  return (
    <button className="fab" onClick={onClick} aria-label="Contact us">
      <span className="pulse" />
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ position: 'relative', zIndex: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.25))' }}>
        <path
          d="M12 3C7 3 3 6.6 3 10.9c0 2.3 1.2 4.4 3.1 5.8L5.2 20.5l3.5-1.9c.9.3 1.9.5 2.9.5 5 0 9-3.6 9-8S17 3 12 3z"
          fill="#fff"
        />
        <circle cx="9" cy="11" r="1.4" fill="#EE2B1B" />
        <circle cx="12" cy="11" r="1.4" fill="#EE2B1B" />
        <circle cx="15" cy="11" r="1.4" fill="#EE2B1B" />
      </svg>
    </button>
  );
}

// ===== Action Sheet =====
export function ActionSheet({ config, onClose }: { config: SiteConfig | null; onClose: () => void }) {
  if (!config) return null;

  const copyViber = () => {
    navigator.clipboard?.writeText(config.viber).then(() => {
      showToast('Viber ID copied');
    }).catch(() => {
      showToast('Copy failed, please copy manually: ' + config.viber);
    });
  };

  const callPhone = () => {
    window.location.href = `tel:${config.phone}`;
  };

  return (
    <div className="sheet-mask" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="grab" />
        <div className="sheet-title">Contact Us by Viber or Phone</div>
        <div className="sheet-option" onClick={copyViber}>
          <div className="icon viber">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3C7 3 3 6.6 3 10.9c0 2.3 1.2 4.4 3.1 5.8L5.2 20.5l3.5-1.9c.9.3 1.9.5 2.9.5 5 0 9-3.6 9-8S17 3 12 3z" />
            </svg>
          </div>
          <div className="text">
            <div className="label">Copy Viber ID</div>
            <div className="val">{config.viber}</div>
          </div>
        </div>
        <div className="sheet-option" onClick={callPhone}>
          <div className="icon phone">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z" />
            </svg>
          </div>
          <div className="text">
            <div className="label">Make a Call</div>
            <div className="val">{config.phone}</div>
          </div>
        </div>
        <button className="sheet-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ===== Toast (imported from hooks) =====
import { showToast } from '../hooks/useApi';
export { showToast };

// ===== Skeletons =====
export function BrandGridSkeleton() {
  return (
    <div className="brand-grid">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton" style={{ aspectRatio: 1, borderRadius: 'var(--r-card)' }} />
      ))}
    </div>
  );
}

export function ModelListSkeleton() {
  return (
    <div className="model-list">
      {[1, 2, 3].map((i) => (
        <div key={i} className="model-card">
          <div className="skeleton" style={{ width: 94, height: 94, borderRadius: 'var(--r-sm)' }} />
          <div className="info">
            <div className="skeleton" style={{ width: 60, height: 12 }} />
            <div className="skeleton" style={{ width: 120, height: 16 }} />
            <div className="skeleton" style={{ width: 80, height: 20 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ColorGridSkeleton() {
  return (
    <div className="color-grid">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton" style={{ aspectRatio: 1, borderRadius: 'var(--r-card)' }} />
      ))}
    </div>
  );
}

export function ProductSkeleton() {
  return (
    <div style={{ padding: '0 var(--s-4)' }}>
      <div className="skeleton" style={{ aspectRatio: '1/1', borderRadius: 'var(--r-hero)', marginBottom: 'var(--s-3)' }} />
      <div className="skeleton" style={{ width: '100%', height: 80, borderRadius: 'var(--r-card)' }} />
    </div>
  );
}

// ===== Empty State =====
export function EmptyState({ title, desc }: { title: string; desc?: string }) {
  return (
    <div className="empty-state">
      <svg className="icon" viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="40" r="30" fill="currentColor" opacity="0.07" />
        <path
          d="M26 33h28l2 26a3 3 0 0 1-3 3.3H27a3 3 0 0 1-3-3.3l2-26Z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path d="M31 33v-3a9 9 0 0 1 18 0v3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <div className="title">{title}</div>
      {desc && <div className="desc">{desc}</div>}
    </div>
  );
}

// ===== Error State =====
export function ErrorState({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div className="error-state">
      <div className="icon">
        <svg viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="20" fill="currentColor" opacity="0.10" />
          <path d="M24 14v15" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
          <circle cx="24" cy="34" r="2.4" fill="currentColor" />
        </svg>
      </div>
      <div className="msg">{msg}</div>
      <button className="retry" onClick={onRetry}>Retry</button>
    </div>
  );
}

// ===== Format Price =====
export function formatPrice(price: number): string {
  return price.toLocaleString('en-US');
}
